import type * as React from 'react';

import type {
  EnterpriseUserAdminView_ActivityFilterInput,
  EnterpriseUserAdminView_AdditionalAttributeSelectionInput,
  EnterpriseUserAdminView_ColumnName,
  EnterpriseUserAdminView_DateComparisonInput,
  EnterpriseUserAdminView_FilterResultUserSelectionInput,
  EnterpriseUserAdminView_PrimaryFilterInput,
  EnterpriseUserAdminView_ProductIdInput,
  EnterpriseUserAdminView_SortableColumnInput,
  EnterpriseUserAdminView_UserFilterInput,
  EnterpriseUserAdminView_UserIdentifierInput,
} from '__generated__/graphql-types';
import { EnterpriseUserAdminView_UserProgramMembershipStatus } from '__generated__/graphql-types';
import moment from 'moment';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import type { FilterQueryData, SortBy } from 'bundles/enterprise-admin-messages/types';
import {
  FilterOptionType,
  Flags,
  InvitationState,
  OperatorType,
  QueryType,
} from 'bundles/enterprise-admin-messages/types';
import { expandAttributes } from 'bundles/enterprise-admin-messages/utils/expandAttributes';
import {
  DEFAULT_FOR_INACTIVITY,
  DEFAULT_FOR_LAST_COMPLETION_WITHIN,
  getPresetFilters,
} from 'bundles/enterprise-admin-user-filters/constants';
import type {
  ActivityFilter,
  ActivityFilterMapped,
  ActivityFilterType,
  AdditionalAttributeFilter,
  AdditionalFilterType,
  DateClause,
  EnrichedData,
  EnrichedProduct,
  FilterResultUserSelection,
  PrimaryFilter,
  PrimaryFilterMapped,
  Product,
  ProductType,
  SavedAdminFilter,
  SavedAdminFilterFromServer,
  UserFilter,
  UserFilterMapped,
} from 'bundles/enterprise-admin-user-filters/types';
import type { ProgramScope } from 'bundles/enterprise-admin-users/contextUtils';
import type { AttributeSelections } from 'bundles/enterprise-admin-users/types';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const getTimestampForLastNDays = (days?: number) => {
  const timestamp = moment().valueOf();

  return moment(timestamp)
    .subtract(days ?? 0, 'days')
    .valueOf();
};

export const getTimestampFromDate = (date: string) => {
  return moment(date).valueOf();
};

export const getDateFromTimestamp = (date?: number): string | undefined => {
  if (date === undefined) return;
  return moment(date).format(MOMENT_FORMATS.YEAR_MONTH_DAY);
};

export const getDays = (dateClause?: DateClause) => {
  return dateClause?.type === 'RELATIVE' ? dateClause.nDaysAgoAfter || dateClause.nDaysAgoBefore : undefined;
};

export const getAfterTimestamp = (dateClause?: DateClause) => {
  return dateClause?.type === 'ABSOLUTE' ? dateClause.after : undefined;
};

export const getBeforeTimestamp = (dateClause?: DateClause) => {
  return dateClause?.type === 'ABSOLUTE' ? dateClause.before : undefined;
};

const getDateIntegerFilter = (dateClause?: DateClause, daysOperator = OperatorType.LESS_THAN) => {
  if (!dateClause) {
    return undefined;
  }

  if (dateClause.type === 'RELATIVE') {
    return [
      {
        operator: daysOperator,
        value: getTimestampForLastNDays(dateClause.nDaysAgoAfter),
      },
    ];
  } else if (dateClause.after && dateClause.before) {
    return [
      {
        operator: OperatorType.GREATER_THAN,
        value: dateClause.after,
      },
      {
        operator: OperatorType.LESS_THAN,
        value: dateClause.before,
      },
    ];
  } else if (dateClause.after) {
    return [
      {
        operator: OperatorType.GREATER_THAN,
        value: dateClause.after,
      },
    ];
  }
};

export const normalizeProgramId = (program: { id: string }) => program.id.replace(/^program~/, '');

const normalizeProductId = (option: FilterOption) =>
  option.id
    .replace(/^product~/, '')
    .replace(/^VerifiedCertificate!~/, '')
    .replace(/^Specialization!~/, '');

const mapEnrichedProductType = (option: FilterOption) => {
  if (option.id.includes('VerifiedCertificate')) {
    return 'PRODUCT_TYPE_VERIFIED_CERTIFICATE';
  }
  if (option.id.includes('Specialization')) {
    return 'PRODUCT_TYPE_SPECIALIZATION';
  }
  return 'UNRECOGNIZED';
};

export const mapToOldProductType = (type: ProductType) => {
  return {
    PRODUCT_TYPE_VERIFIED_CERTIFICATE: 'VerifiedCertificate',
    PRODUCT_TYPE_SPECIALIZATION: 'Specialization',
    UNRECOGNIZED: 'UNRECOGNIZED',
    PRODUCT_TYPE_INVALID: 'PRODUCT_TYPE_INVALID',
  }[type];
};

export const getNumEnrollments = (type?: ActivityFilterType): FilterQueryData['params']['numEnrollments'] => {
  if (type === 'ENROLLED_BUT_INACTIVE' || type === 'ENROLLED') {
    return [{ operator: OperatorType.GREATER_THAN, value: 0 }];
  } else if (type === 'NO_ENROLLMENTS') {
    return [{ operator: OperatorType.EQUALS, value: 0 }];
  }
};

export const mapFilterParams = (
  userFilter: UserFilter,
  orgId: string,
  searchNamesOrEmails: string[] = [],
  sortBy: SortBy[] = [],
  attributeSelection: AttributeSelections[] = [],
  userIdentifiers: string[] = [],
  programScope: ProgramScope['programScope'] = { isProgramScope: false, programIds: [] },
  canManageOrganization = false,
  programIdsToManage: string[] = []
): FilterQueryData => {
  const getAdditionalAttributeValue = (attributeName: AdditionalFilterType) =>
    userFilter.additionalAttributeSelections?.find((_) => _.attributeName === attributeName)?.values;

  const flags = [];
  if (programScope.isProgramScope || !canManageOrganization) {
    flags.push(Flags.programLevel);
  }
  let programIds: string[];
  if (programScope.isProgramScope) {
    programIds = programScope.programIds;
  } else {
    programIds =
      userFilter.primaryFilter?.type === 'MULTI_TYPE_FILTER'
        ? (userFilter.primaryFilter?.multiFilter?.programIdSelection?.values ?? [])
        : (userFilter.primaryFilter?.programIdSelection?.values ?? []);
  }
  if (!canManageOrganization && programIds.length === 0) {
    programIds = programIdsToManage;
  }

  const type = userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter?.activityFilter?.type : undefined;

  const numEnrollments = getNumEnrollments(type);

  const filterQueryData: FilterQueryData = {
    queryType: userFilter.primaryFilter?.type === 'INVITED' ? QueryType.INVITEES : QueryType.MEMBERS,
    flags,
    params: {
      orgId,
      invitationStates: userFilter.primaryFilter?.type === 'INVITED' ? [InvitationState.ACTIVE] : undefined,
      completionTimes:
        userFilter.primaryFilter?.type === 'MEMBER' &&
        userFilter.primaryFilter?.activityFilter?.type === 'COMPLETED' &&
        userFilter.primaryFilter?.activityFilter.completionDate?.type === 'RELATIVE'
          ? [
              {
                operator: OperatorType.GREATER_THAN,
                value: getTimestampForLastNDays(
                  userFilter.primaryFilter?.activityFilter.completionDate?.nDaysAgoAfter ??
                    DEFAULT_FOR_LAST_COMPLETION_WITHIN
                ),
              },
            ]
          : undefined,
      numEnrollments,
      deletedAt:
        userFilter.primaryFilter?.type === 'REMOVED_MEMBER'
          ? [
              {
                operator: OperatorType.GREATER_THAN,
                value: 0,
              },
            ]
          : undefined,
      latestActivityAt:
        userFilter.primaryFilter?.type === 'MEMBER'
          ? getDateIntegerFilter(userFilter.primaryFilter?.activityFilter?.lastActivityDate, OperatorType.LESS_THAN)
          : undefined,

      productIds:
        userFilter.primaryFilter?.type === 'MEMBER'
          ? userFilter.primaryFilter?.activityFilter?.productIdSelection?.values.map(
              ({ productItemId, productType }) => `${productType}~${productItemId}`
            )
          : undefined,
      productEnrolledAt:
        userFilter.primaryFilter?.type === 'MEMBER' &&
        (type === 'ENROLLED' || type === 'ENROLLED_BUT_INACTIVE' || type === 'COMPLETED')
          ? getDateIntegerFilter(userFilter.primaryFilter?.activityFilter?.enrollmentDate)
          : undefined,
      programIds,
      programJoinedAt:
        userFilter.primaryFilter?.type === 'MEMBER'
          ? getDateIntegerFilter(userFilter.primaryFilter?.programJoinDate, OperatorType.GREATER_THAN)
          : undefined,

      contractIds: getAdditionalAttributeValue('CONTRACT')?.map((id) => id.replace(/^contract~/, '')),
      businessUnits: getAdditionalAttributeValue('BUSINESS_UNIT'),
      locations: getAdditionalAttributeValue('LOCATION'),
      managers: getAdditionalAttributeValue('MANAGER'),
      jobTitles: getAdditionalAttributeValue('JOB_TITLE'),
      searchNamesOrEmails,
      userIdentifiers,
    },
    sortBy,
    attributeSelection: expandAttributes(attributeSelection),
  };

  return filterQueryData;
};

// TODO stringify the filter
export const getUserFilterDetails = (userFilter: UserFilter) =>
  [
    userFilter.primaryFilter?.type,
    userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter?.activityFilter?.type : undefined,
  ].filter((_) => _);

export const createActivityFilter = (filterType: ActivityFilterType) => {
  const activityFilter: ActivityFilter = {
    type: filterType,
  };
  if (filterType === 'COMPLETED') {
    activityFilter.completionDate = { type: 'RELATIVE', nDaysAgoAfter: DEFAULT_FOR_LAST_COMPLETION_WITHIN };
  }
  if (filterType === 'GENERAL_INACTIVITY' || filterType === 'ENROLLED_BUT_INACTIVE') {
    activityFilter.lastActivityDate = { type: 'RELATIVE', nDaysAgoBefore: DEFAULT_FOR_INACTIVITY };
  }
  return activityFilter;
};

export const eventuallyAutoFocus = (ref: React.MutableRefObject<HTMLDivElement | null>) =>
  setTimeout(() => {
    (ref.current?.querySelector('button') as HTMLButtonElement)?.focus();
  }, 0);

export const COMMON_DATE_FORMAT = 'MM/DD/YYYY';
export const formatDate = (date: string | number) => formatDateTimeDisplay(date, COMMON_DATE_FORMAT);

const isId = (_: string) => !String(_).includes('@');
const parseUserId = (_: string | number) => Number(String(_).replace(/^userId~/, ''));

// I think ideally this is something that we can get rid of or reduce by changing how the filter params are saved on the FE
export const mapUserFilterFromClientToServerModel = (
  userFilter: UserFilter,
  options?: {
    searchNameOrEmails?: string[];
    sortBy?: SortBy[];
    selectedUserIds?: string[];
    selectedUserEmails?: string[];
    userIdentifiers?: string[];
  }
): UserFilterMapped => {
  let primaryFilter: PrimaryFilterMapped;
  let programJoinDate: DateClause | undefined;
  let activityFilter: ActivityFilterMapped | undefined;
  let productIdSelection: { values: Product[] } | undefined;

  let programIdSelection =
    userFilter.primaryFilter?.type === 'MULTI_TYPE_FILTER'
      ? userFilter.primaryFilter?.multiFilter?.programIdSelection?.values
      : userFilter.primaryFilter?.programIdSelection?.values;
  if (programIdSelection && programIdSelection.length === 0) {
    programIdSelection = undefined;
  }
  if (userFilter.primaryFilter?.type === 'INVITED') {
    primaryFilter = {
      ...(programIdSelection && { programIdSelection: { values: programIdSelection } }),
      type: 'INVITED',
    };
  } else if (userFilter.primaryFilter?.type === 'REMOVED_MEMBER') {
    primaryFilter = {
      ...(programIdSelection && { programIdSelection: { values: programIdSelection } }),
      type: 'REMOVED_MEMBER',
    };
  } else if (userFilter.primaryFilter?.type === 'MULTI_TYPE_FILTER') {
    // Expected schema for saved filter API (CreateAdminUserFilter):
    // primaryFilter: { type: 'MULTI_TYPE_FILTER', primaryFilterTypes: ['INVITED','MEMBER'], programIdSelection, activityFilter }
    // Derive primaryFilterTypes from selected membershipStates on the learners page.
    // Map UI membership states to CreateAdminUserFilter primaryFilterTypes:
    //  INVITED -> INVITED, JOINED -> MEMBER, REMOVED -> REMOVED_MEMBER
    const selectedMembershipStates = userFilter.primaryFilter.multiFilter?.membershipStates ?? [];
    const derivedPrimaryFilterTypes = selectedMembershipStates
      .map(
        (state) =>
          (({ INVITED: 'INVITED', JOINED: 'MEMBER', REMOVED: 'REMOVED_MEMBER' }) as const)[
            state as 'INVITED' | 'JOINED' | 'REMOVED'
          ]
      )
      .filter(Boolean) as ('INVITED' | 'MEMBER' | 'REMOVED_MEMBER')[];

    primaryFilter = {
      type: 'MULTI_TYPE_FILTER',
      // Fallback to default if nothing is selected to preserve previous behavior
      primaryFilterTypes: (derivedPrimaryFilterTypes.length > 0
        ? derivedPrimaryFilterTypes
        : (['INVITED', 'MEMBER'] as const)) as any,
      ...(userFilter.primaryFilter.multiFilter?.programIdSelection && {
        programIdSelection: { values: userFilter.primaryFilter.multiFilter.programIdSelection.values },
      }),
      ...(userFilter.primaryFilter.multiFilter?.activityFilter && {
        activityFilter: userFilter.primaryFilter.multiFilter.activityFilter,
      }),
    } as unknown as PrimaryFilterMapped;
  } else {
    programJoinDate = userFilter.primaryFilter?.programJoinDate;
    productIdSelection = userFilter.primaryFilter?.activityFilter?.productIdSelection;
    if (productIdSelection?.values?.length === 0) {
      productIdSelection = undefined;
    }

    const lastActivityDate = userFilter.primaryFilter?.activityFilter?.lastActivityDate;
    const enrollmentDate = userFilter.primaryFilter?.activityFilter?.enrollmentDate;
    const completionDate = userFilter.primaryFilter?.activityFilter?.completionDate;
    const assignedContent = userFilter.primaryFilter?.activityFilter?.assignedContent;
    // Mapping activityFilter
    if (userFilter.primaryFilter?.activityFilter?.type === 'NO_ENROLLMENTS') {
      activityFilter = {
        type: 'NO_ENROLLMENTS',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'GENERAL_INACTIVITY' && lastActivityDate) {
      activityFilter = {
        lastActivityDate,
        type: 'GENERAL_INACTIVITY',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'ENROLLED') {
      activityFilter = {
        ...(enrollmentDate && { enrollmentDate }),
        ...(productIdSelection && { productIdSelection }),
        type: 'ENROLLED',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'ENROLLED_BUT_INACTIVE' && lastActivityDate) {
      activityFilter = {
        ...(enrollmentDate && { enrollmentDate }),
        lastActivityDate,
        ...(productIdSelection && { productIdSelection }),
        type: 'ENROLLED_BUT_INACTIVE',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'COMPLETED' && completionDate) {
      activityFilter = {
        ...(enrollmentDate && { enrollmentDate }),
        ...(productIdSelection && { productIdSelection }),
        completionDate,
        type: 'COMPLETED',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'ASSIGNED_CONTENT' && assignedContent) {
      activityFilter = {
        contentSelection: assignedContent.contentSelection,
        isCompleted: assignedContent.isCompleted,
        isEnrolled: assignedContent.isEnrolled,
        type: 'ASSIGNED_CONTENT',
      };
    } else if (userFilter.primaryFilter?.activityFilter?.type === 'NOT_ENROLLED_OR_NOT_COMPLETED') {
      activityFilter = {
        type: 'NOT_ENROLLED_OR_NOT_COMPLETED',
        productIdSelection: {
          values: productIdSelection?.values ?? [],
        },
      };
    }

    primaryFilter = {
      ...(programIdSelection && { programIdSelection: { values: programIdSelection } }),
      ...(programJoinDate && { programJoinDate }),
      ...(activityFilter && { activityFilter }),
      type: 'MEMBER',
    };
  }

  let additionalAttributeSelections: UserFilterMapped['additionalAttributeSelections'] =
    userFilter?.additionalAttributeSelections;
  if (additionalAttributeSelections) {
    additionalAttributeSelections = additionalAttributeSelections.filter((_) => _.values.length > 0);

    if (additionalAttributeSelections.length === 0) {
      additionalAttributeSelections = undefined;
    }
  }

  const mappedSortBy = options?.sortBy?.map((_) => ({ columnName: _.column, sortDirection: _.direction }));

  // Explicit overrides (a manual learners-table selection, or a search-box term) take
  // priority; otherwise fall back to whatever the input `userFilter` itself already
  // carries (e.g. `mapUserFilterFromServerToClientModel`'s output when round-tripping an
  // existing saved filter — see the ADMIN_USER_FILTER resend flow in
  // enterprise-admin-messaging-v2) so recreating a filter doesn't silently drop the
  // specific users/search term it was originally scoped to.
  const hasExplicitUserSelection = Boolean(
    options?.userIdentifiers?.length || options?.selectedUserIds?.length || options?.selectedUserEmails?.length
  );
  const filteredSelectedUserIds =
    (options?.userIdentifiers?.filter(isId) ?? options?.selectedUserIds)?.map((userId) => ({
      userId: parseUserId(userId),
    })) ?? [];
  const filteredSelectedUserEmails =
    (options?.userIdentifiers?.filter((_) => !isId(_)) ?? options?.selectedUserEmails)?.map((email) => ({
      email: email.replace(/^email~/, ''),
    })) ?? [];

  const filterResultUserSelection: FilterResultUserSelection = hasExplicitUserSelection
    ? { userIdentifiers: [...filteredSelectedUserIds, ...filteredSelectedUserEmails] }
    : (userFilter.filterResultUserSelection ?? { userIdentifiers: [] });

  const searchNameOrEmails = options?.searchNameOrEmails?.length
    ? options.searchNameOrEmails
    : userFilter.searchNameOrEmails;

  return {
    primaryFilter,
    ...(additionalAttributeSelections && { additionalAttributeSelections }),
    ...(searchNameOrEmails?.length && { searchNameOrEmails }),
    ...(mappedSortBy?.length && { sortBy: mappedSortBy }),
    ...(filterResultUserSelection.userIdentifiers.length && { filterResultUserSelection }),
    type: 'USER_V1',
  };
};

const mapUserIdentifier = (
  userIdentifier: { email: string } | { userId: number }
): EnterpriseUserAdminView_UserIdentifierInput | undefined => {
  if ('email' in userIdentifier) {
    return userIdentifier as EnterpriseUserAdminView_UserIdentifierInput;
  } else if ('userId' in userIdentifier) {
    return { userId: userIdentifier.userId.toString() } as EnterpriseUserAdminView_UserIdentifierInput;
  } else {
    return undefined;
  }
};

const mapFilterResultUserSelection = (
  filterResultUserSelection: FilterResultUserSelection | undefined
): EnterpriseUserAdminView_FilterResultUserSelectionInput | undefined => {
  if (!filterResultUserSelection) {
    return undefined;
  }
  const filtered = filterResultUserSelection.userIdentifiers
    .map(mapUserIdentifier)
    .filter((_) => _ !== undefined) as EnterpriseUserAdminView_UserIdentifierInput[];
  return {
    userIdentifiers: filtered,
  };
};

export const mapUserFilterFromServerToClientModel = (savedAdminFilter: SavedAdminFilterFromServer): UserFilter => {
  const mappedFilter: UserFilter = {};
  const userFilter = savedAdminFilter.filter;

  if (userFilter.primaryFilter.type === 'REMOVED_MEMBER') {
    mappedFilter.primaryFilter = {
      type: 'REMOVED_MEMBER',
      programIdSelection: {
        values: userFilter.primaryFilter.programIdSelection?.values ?? [],
      },
    };
  } else if (userFilter.primaryFilter.type === 'INVITED') {
    mappedFilter.primaryFilter = {
      type: 'INVITED',
      programIdSelection: {
        values: userFilter.primaryFilter.programIdSelection?.values ?? [],
      },
    };
  } else if (userFilter.primaryFilter.type === 'MULTI_TYPE_FILTER') {
    mappedFilter.primaryFilter = {
      type: 'MULTI_TYPE_FILTER',
      multiFilter: {
        programIdSelection: {
          values: userFilter.primaryFilter.multiFilter?.programIdSelection?.values ?? [],
        },
      },
    };
  } else {
    mappedFilter.primaryFilter = {
      type: 'MEMBER',
      programJoinDate: userFilter.primaryFilter.programJoinDate,
      programIdSelection: {
        values: userFilter.primaryFilter.programIdSelection?.values ?? [],
      },
    };
    if (userFilter.primaryFilter.activityFilter) {
      mappedFilter.primaryFilter.activityFilter = {
        type: userFilter.primaryFilter.activityFilter.type,
      };
      if (
        userFilter?.primaryFilter?.activityFilter?.type === 'GENERAL_INACTIVITY' ||
        userFilter?.primaryFilter?.activityFilter?.type === 'ENROLLED_BUT_INACTIVE'
      ) {
        mappedFilter.primaryFilter.activityFilter.lastActivityDate =
          userFilter?.primaryFilter?.activityFilter?.lastActivityDate;
      }
      if (
        userFilter?.primaryFilter?.activityFilter?.type === 'ENROLLED' ||
        userFilter?.primaryFilter?.activityFilter?.type === 'ENROLLED_BUT_INACTIVE' ||
        userFilter?.primaryFilter?.activityFilter?.type === 'COMPLETED'
      ) {
        mappedFilter.primaryFilter.activityFilter.enrollmentDate =
          userFilter?.primaryFilter?.activityFilter?.enrollmentDate;
        mappedFilter.primaryFilter.activityFilter.productIdSelection =
          userFilter?.primaryFilter?.activityFilter.productIdSelection;
      }
      if (userFilter?.primaryFilter?.activityFilter?.type === 'COMPLETED') {
        mappedFilter.primaryFilter.activityFilter.completionDate =
          userFilter?.primaryFilter?.activityFilter?.completionDate;
      }
    }
  }

  mappedFilter.additionalAttributeSelections = userFilter.additionalAttributeSelections;
  // Only set when present so callers that round-trip this through `toStrictEqual`-style
  // fixtures without these fields aren't affected — but when the saved filter DOES carry
  // explicit user picks or a search term (e.g. a learners-table selection saved as a
  // filter), a caller recreating a filter from this client model must not silently drop
  // them (see the `ADMIN_USER_FILTER` resend flow in enterprise-admin-messaging-v2, which
  // feeds this mapped filter straight into `mapUserFilterFromClientToServerModel` again).
  if (userFilter.filterResultUserSelection) {
    mappedFilter.filterResultUserSelection = userFilter.filterResultUserSelection;
  }
  if (userFilter.searchNameOrEmails?.length) {
    mappedFilter.searchNameOrEmails = userFilter.searchNameOrEmails;
  }
  return mappedFilter;
};

export const getValidationError = (Name: string) => {
  const name = Name.toLowerCase();
  const presets = getPresetFilters();
  if (!name) {
    return _t('Name is required');
  }
  if (presets.some((_) => _.filterName.toLowerCase() === name || _.programScopeName === name)) {
    return _t('This name is reserved, please choose an alternative name');
  }
};
export const convertEnrichedProgramsToFilterOptions = (enrichedData: EnrichedData, programsIds: string[]) =>
  programsIds.reduce((all, id) => {
    const enrichment = enrichedData?.enrichedPrograms.find((_) => _.programId === id);
    if (enrichment) {
      all.push({
        id: `program~${id}`,
        type: FilterOptionType.PROGRAM,
        label: enrichment.programName,
      });
    }
    return all;
  }, [] as FilterOption[]);

export const convertFilterOptionsToIds = (filterOptions: FilterOption[]): string[] =>
  filterOptions.map(normalizeProgramId);

export const convertFilterOptionsToProducts = (filterOptions: FilterOption[]): Product[] =>
  filterOptions.map((option) => ({
    productItemId: normalizeProductId(option),
    productType:
      (
        {
          [FilterOptionType.COURSE]: 'VerifiedCertificate',
          [FilterOptionType.SPECIALIZATION]: 'Specialization',
        } as const
      )[option.type] || 'VerifiedCertificate',
  }));

export const convertFilterOptionsToEnrichedPrograms = (filterOptions: FilterOption[]) =>
  filterOptions.map((option) => ({
    programId: normalizeProgramId(option),
    programName: option.label,
  }));

export const convertFilterOptionsToEnrichedProducts = (filterOptions: FilterOption[]): EnrichedProduct[] =>
  filterOptions.map((option) => ({
    productType: mapEnrichedProductType(option),
    productItemId: normalizeProductId(option),
    productName: option.label,
  }));

export const convertEnrichedProductsToFilterOptions = (enrichedData: EnrichedData, products: Product[] = []) =>
  products.reduce((all, product) => {
    const enrichment = enrichedData?.enrichedProducts.find((_) => _.productItemId === product.productItemId);
    if (enrichment) {
      all.push({
        id: `product~${product.productType}!~${product.productItemId}`,
        type: {
          VerifiedCertificate: FilterOptionType.COURSE,
          Specialization: FilterOptionType.SPECIALIZATION,
        }[product.productType],
        label: enrichment.productName,
      });
    }
    return all;
  }, [] as FilterOption[]);

export const getContractLabel = (enrichedData: EnrichedData, id: string) =>
  enrichedData?.enrichedContracts.find((_) => _.contractId === id)?.contractName || id;

export const mapContractIdsToFilterOptions = (enrichedData: EnrichedData, ids: string[]) =>
  ids.map((id) => ({
    id: `contract~${id}`,
    label: enrichedData?.enrichedContracts?.find((_) => _.contractId === id)?.contractName ?? id,
    type: 'CONTRACT',
  }));

export const getContractFilterOptions = (enrichedData: EnrichedData, additionalFilters: AdditionalAttributeFilter[]) =>
  mapContractIdsToFilterOptions(
    enrichedData,
    additionalFilters.find((_) => _.attributeName === 'CONTRACT')?.values ?? []
  );

export const normalizeContractId = (id: string) => id.replace(/^contract~/, '');

const mapDateClause = (dateClause: DateClause): EnterpriseUserAdminView_DateComparisonInput => {
  if (dateClause?.type === 'RELATIVE') {
    return {
      relative: {
        nDaysAgoAfter: dateClause?.nDaysAgoAfter,
        nDaysAgoBefore: dateClause?.nDaysAgoBefore,
      },
    };
  } else {
    return {
      absolute: {
        after: dateClause && dateClause.after ? new Date(dateClause.after).toISOString() : null,
        before: dateClause && dateClause.before ? new Date(dateClause.before).toISOString() : null,
      },
    };
  }
};

const mapProduct = (product: Product): EnterpriseUserAdminView_ProductIdInput => {
  const typeMapping = {
    Specialization: 'SPECIALIZATION',
    VerifiedCertificate: 'VERIFIED_CERTIFICATE',
  } as const;
  return {
    productType: typeMapping[product.productType],
    productItemId: product.productItemId,
  };
};

const mapActivityFilter = (
  activityFilter: ActivityFilter | ActivityFilterMapped
): EnterpriseUserAdminView_ActivityFilterInput => {
  switch (activityFilter.type) {
    case 'NO_ENROLLMENTS': {
      return {
        noEnrollments: {},
      };
    }
    case 'GENERAL_INACTIVITY': {
      return {
        generalInactivity: {
          lastActivityDate: mapDateClause(activityFilter.lastActivityDate) ?? null,
        },
      };
    }
    case 'ENROLLED': {
      return {
        enrolled: {
          ...(activityFilter.productIdSelection !== undefined && activityFilter.productIdSelection.values.length > 0
            ? {
                productIdSelection: {
                  values: activityFilter.productIdSelection.values.map(mapProduct),
                },
              }
            : { productIdSelection: null }),
          enrollmentDate: activityFilter.enrollmentDate ? mapDateClause(activityFilter.enrollmentDate) : null,
        },
      };
    }
    case 'ENROLLED_BUT_INACTIVE': {
      return {
        enrolledButInactive: {
          ...(activityFilter.productIdSelection !== undefined && activityFilter.productIdSelection.values.length > 0
            ? {
                productIdSelection: {
                  values: activityFilter.productIdSelection.values.map(mapProduct),
                },
              }
            : { productIdSelection: null }),
          enrollmentDate: activityFilter.enrollmentDate ? mapDateClause(activityFilter.enrollmentDate) : null,
          lastActivityDate: mapDateClause(activityFilter.lastActivityDate),
        },
      };
    }
    case 'COMPLETED': {
      return {
        completed: {
          ...(activityFilter.productIdSelection !== undefined && activityFilter.productIdSelection.values.length > 0
            ? {
                productIdSelection: {
                  values: activityFilter.productIdSelection.values.map(mapProduct),
                },
              }
            : { productIdSelection: null }),
          enrollmentDate: activityFilter.enrollmentDate ? mapDateClause(activityFilter.enrollmentDate) : null,
          completionDate: mapDateClause(activityFilter.completionDate),
        },
      };
    }
    case 'ASSIGNED_CONTENT': {
      return {
        assignedContent: (activityFilter as any).assignedContent || {
          contentSelection: (activityFilter as any).contentSelection,
          isCompleted: (activityFilter as any).isCompleted,
          isEnrolled: (activityFilter as any).isEnrolled,
        },
      };
    }
    case 'NOT_ENROLLED_OR_NOT_COMPLETED': {
      return {
        notEnrolledOrNotCompleted: {
          productIdSelection: {
            values: activityFilter.productIdSelection?.values.map(mapProduct) ?? [],
          },
        },
      };
    }

    default: {
      return {
        assignedContent: null,
        completed: null,
        enrolled: null,
        enrolledButInactive: null,
        generalInactivity: null,
        noEnrollments: null,
      };
    }
  }
};

const mapMembershipState = (membershipState: string) => {
  switch (membershipState) {
    case 'JOINED':
      return EnterpriseUserAdminView_UserProgramMembershipStatus.Joined;
    case 'INVITED':
      return EnterpriseUserAdminView_UserProgramMembershipStatus.Invited;
    case 'REMOVED':
      return EnterpriseUserAdminView_UserProgramMembershipStatus.Removed;
    default:
      throw new Error(`Invalid membership state: ${membershipState}`);
  }
};

export const mapPrimaryFilter = (
  primaryFilter?: PrimaryFilter | PrimaryFilterMapped
): EnterpriseUserAdminView_PrimaryFilterInput => {
  if (!primaryFilter) {
    return {};
  }
  switch (primaryFilter.type) {
    case 'MULTI_TYPE_FILTER':
      return {
        multiFilter: {
          activityFilter: primaryFilter.multiFilter?.activityFilter
            ? mapActivityFilter(primaryFilter.multiFilter.activityFilter)
            : null,
          programIdSelection:
            primaryFilter.multiFilter?.programIdSelection &&
            primaryFilter.multiFilter.programIdSelection.values &&
            primaryFilter.multiFilter.programIdSelection.values.length > 0
              ? primaryFilter.multiFilter.programIdSelection
              : null,
          membershipStates: primaryFilter.multiFilter?.membershipStates
            ? primaryFilter.multiFilter?.membershipStates.map(mapMembershipState)
            : [],
        },
      };
    case 'MEMBER':
      return {
        member: {
          programIdSelection:
            primaryFilter.programIdSelection &&
            primaryFilter.programIdSelection.values &&
            primaryFilter.programIdSelection.values.length > 0
              ? primaryFilter.programIdSelection
              : null,
          programJoinDate: primaryFilter.programJoinDate ? mapDateClause(primaryFilter.programJoinDate) : null,
          activityFilter: primaryFilter.activityFilter ? mapActivityFilter(primaryFilter.activityFilter) : null,
        },
      };
    case 'INVITED':
      return {
        invited: {
          programIdSelection:
            primaryFilter.programIdSelection &&
            primaryFilter.programIdSelection.values &&
            primaryFilter.programIdSelection.values.length > 0
              ? primaryFilter.programIdSelection
              : null,
        },
      };
    case 'REMOVED_MEMBER':
      return {
        removedMember: {
          programIdSelection:
            primaryFilter.programIdSelection &&
            primaryFilter.programIdSelection.values &&
            primaryFilter.programIdSelection.values.length > 0
              ? primaryFilter.programIdSelection
              : null,
        },
      };
    default:
      throw new Error("Invalid type for 'primaryFilter'");
  }
};

const mapAdditionalFilter = (
  additionalFilter: AdditionalAttributeFilter
): EnterpriseUserAdminView_AdditionalAttributeSelectionInput => {
  return {
    attributeName: additionalFilter.attributeName,
    values: additionalFilter.values,
  };
};

const mapAttributeSelection = (
  attributeSelection: AttributeSelections
): EnterpriseUserAdminView_ColumnName | undefined => {
  switch (attributeSelection) {
    case 'NAME':
      return 'NAME';
    case 'NUM_ENROLLMENTS':
      return 'NUM_ENROLLMENTS';
    case 'NUM_COMPLETIONS':
      return 'NUM_COMPLETIONS';
    case 'BUSINESS_UNITS':
      return 'BUSINESS_UNIT';
    case 'MANAGER':
      return 'MANAGER_NAME';
    case 'INVITED_AT':
      return 'INVITED_AT';
    case 'JOINED_AT':
      return 'JOINED_AT';
    case 'LATEST_ACTIVITY_AT':
      return 'LATEST_ACTIVITY_AT';
    case 'DELETED_AT':
      return 'DELETED_AT';
    case 'IS_SOFT_DELETED':
      return 'IS_SOFT_DELETED';
    default:
      return undefined;
  }
};

const mapSortBy = (sortBy: SortBy): EnterpriseUserAdminView_SortableColumnInput => {
  const typeMapping = {
    ASC: 'ASCENDING',
    DESC: 'DESCENDING',
  } as const;
  return {
    columnName: mapAttributeSelection(sortBy.column)!,
    sortDirection: typeMapping[sortBy.direction],
  };
};

export const mapAdditionalAttributeSelections = (additionalAttributeSelections: AdditionalAttributeFilter[]) => {
  const filteredAttributes = additionalAttributeSelections
    .filter((attribute) => attribute.values.length > 0)
    .map(mapAdditionalFilter);
  return filteredAttributes.length > 0 ? filteredAttributes : null;
};

const mapUserSelection = (userSelections: string[]) => {
  const formattedSelections = userSelections
    .map((selection) => {
      if (selection.includes('userId')) {
        return { userId: parseUserId(selection).toString() };
      } else if (selection.includes('email')) {
        return { email: selection.replace(/^email~/, '') };
      } else if (!isNaN(Number(selection))) {
        return { userId: selection };
      } else {
        return undefined;
      }
    })
    .filter((_) => _ !== undefined);

  return {
    userIdentifiers: formattedSelections,
  };
};

export const mapUserFilter = (
  userFilter: UserFilter | UserFilterMapped | undefined,
  contentAssignmentSearch?: string,
  sortBy?: SortBy[] | undefined,
  searchNameOrEmails?: string | undefined,
  userIdentifiers?: string[] | undefined
): EnterpriseUserAdminView_UserFilterInput => {
  if (!userFilter) {
    return {
      primaryFilter: {},
    };
  }

  let calculatedSearchNameOrEmails;
  if (searchNameOrEmails) {
    calculatedSearchNameOrEmails = [searchNameOrEmails];
  } else if (userFilter.searchNameOrEmails && userFilter.searchNameOrEmails.length > 0) {
    calculatedSearchNameOrEmails = userFilter.searchNameOrEmails;
  } else {
    calculatedSearchNameOrEmails = undefined;
  }

  return {
    primaryFilter: mapPrimaryFilter(userFilter.primaryFilter),
    additionalAttributeSelections: userFilter.additionalAttributeSelections
      ? mapAdditionalAttributeSelections(userFilter.additionalAttributeSelections)
      : null,
    ...(contentAssignmentSearch && { searchNameOrEmails: [contentAssignmentSearch] }),
    ...(sortBy &&
      sortBy.length > 0 && { sortBy: sortBy.filter((sort) => !!mapAttributeSelection(sort.column)).map(mapSortBy) }),
    ...(calculatedSearchNameOrEmails && { searchNameOrEmails: calculatedSearchNameOrEmails }),
    filterResultUserSelection: userIdentifiers
      ? mapUserSelection(userIdentifiers)
      : mapFilterResultUserSelection(userFilter.filterResultUserSelection),
  };
};

export const mapContentSelectionValueToGRPC = (_: { contentId: string; contentType: string }) => ({
  contentId: _.contentId,
  contentType:
    (
      {
        COURSE: 'Course',
        SPECIALIZATION: 'Specialization',
        LEARNING_PATH: 'LearningPath',
      } as const
    )[_.contentType] ?? 'Unrecognized',
});

export const buildAssignedContentFilter = ({
  programId,
  programIds,
  programName,
  contentId,
  contentType,
  contentName,
  isCompleted,
  isEnrolled,
}: {
  /** Single-group scope. Superseded by `programIds` when both are given. */
  programId?: string;
  /** Multi-group scope — e.g. every group an admin manages that's eligible for `contentId`. */
  programIds?: string[];
  programName?: string;
  contentId: string;
  contentType: 'COURSE' | 'SPECIALIZATION' | 'LEARNING_PATH';
  contentName?: string;
  isCompleted?: boolean;
  isEnrolled?: boolean;
}): SavedAdminFilter => {
  const resolvedProgramIds = programIds ?? (programId ? [programId] : []);
  return {
    filterId: 'assigned-learners',
    filterName: 'Assigned learners',
    enrichedData: {
      enrichedPrograms:
        resolvedProgramIds.length > 0
          ? resolvedProgramIds.map((id) => ({ programId: id, programName: programName ?? '' }))
          : [{ programId: '', programName: programName ?? '' }],
      enrichedProducts: [
        {
          productType: 'UNRECOGNIZED',
          productItemId: contentId ?? '',
          productName: contentName ?? '',
        },
      ],
      enrichedContracts: [],
    },
    filter: {
      primaryFilter: {
        type: 'MEMBER',
        programIdSelection: {
          values: resolvedProgramIds,
        },
        activityFilter: {
          type: 'ASSIGNED_CONTENT',
          assignedContent: {
            contentSelection: {
              values: [
                {
                  contentId,
                  contentType,
                },
              ],
            },
            isCompleted,
            isEnrolled,
          },
        },
      },
    },
  };
};
